/*
 * datafeed api는 위젯 생성자에서 구현하고 datafeed 객체에 할당해야 하는 메서드 집합
 */
import { TRADINGVIEW_SYMBOL_INFO_LIST, ALL_SYMBOLS, buildSymbolInfoFromMetadata, } from '@template/types';
import { subscribeOnStream, unsubscribeFromStream } from './streaming';
import { useAccountStore, useMetadataStore } from '@template/stores';
const lastBarsCache = new Map();
/**
 * API limit 상한
 * @description getCandleChart 한 번의 요청당 최대 bars 개수
 * FIXME: API 제한 추가 시 수정 필요
 * 현재 API에서의 제한이 없으나, Open API 제공 과정에서 제한을 추가할 필요가 있어보여 임시로 설정
 */
const API_LIMIT_MAX = 5000;
/**
 * TradingView 권장 기본 요청 개수
 */
const DEFAULT_LIMIT = 300;
/**
 * getBars 요청 시 API limit 계산
 * - countBack 우선 사용, 없으면 DEFAULT_LIMIT
 * - 최소 1, 최대 API_LIMIT_MAX
 */
function resolveRequestLimit(countBack) {
    const requested = countBack ?? DEFAULT_LIMIT;
    return Math.min(Math.max(requested, 1), API_LIMIT_MAX);
}
/**
 * Datafeed 생성 함수
 * @param quoteService - QuoteService 인스턴스
 * @returns TradingViewDatafeed 인스턴스 (unsubscribeCurrentBars 포함)
 */
export function createDatafeed(quoteService) {
    let resetCacheCallback;
    let currentSubscriberUID = null;
    const datafeed = {
        onReady: (callback) => {
            setTimeout(() => callback(configurationData));
        },
        // TODO: Chart Widget 내장 검색 기능 불필요 시 내부 로직 제거
        searchSymbols: async (userInput, exchange, symbolType, onResultReadyCallback) => {
            onResultReadyCallback(ALL_SYMBOLS);
        },
        resolveSymbol: async (symbolName, onSymbolResolvedCallback, onResolveErrorCallback, extension) => {
            // 정적 목록에 있으면 사용, 없으면 메타데이터로 동적 생성 (FR40, DE30 등)
            let symbolInfo = TRADINGVIEW_SYMBOL_INFO_LIST[symbolName];
            if (!symbolInfo) {
                const metadataStore = useMetadataStore();
                const metadata = metadataStore.getMetadata(symbolName);
                symbolInfo = buildSymbolInfoFromMetadata(symbolName, metadata?.stockGroupCd);
            }
            setTimeout(() => onSymbolResolvedCallback(symbolInfo));
        },
        getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
            const { from, to, countBack, firstDataRequest } = periodParams;
            try {
                let bars = [];
                const accountStore = useAccountStore();
                const accountNo = accountStore.selectedAccount;
                const limit = resolveRequestLimit(countBack);
                // API: offset 없으면 현재시간, offset 있으면 해당 시간부터 과거 limit개 (offset 단위: ms)
                // firstDataRequest: offset=to (요청 구간의 오른쪽 끝 = 최근 시점)
                // subsequent: offset=to (요청 구간의 오른쪽 끝 = 이미 로드된 가장 왼쪽 bar 직전)
                // PeriodParams.from/to: TradingView는 초 단위 전달. API는 ms 기대하므로 * 1000
                const offsetMs = to * 1000;
                const response = await quoteService.getCandleChart(symbolInfo.ticker, {
                    interval: resolution,
                    offset: offsetMs.toString(),
                    limit,
                    accountNo,
                });
                const apiBars = response.data?.contents || [];
                bars = apiBars.map((bar) => ({
                    time: bar.time,
                    open: bar.open,
                    high: bar.high,
                    low: bar.low,
                    close: bar.close,
                }));
                if (firstDataRequest && bars.length > 0) {
                    lastBarsCache.set(symbolInfo.full_name || symbolInfo.name, {
                        ...bars[bars.length - 1],
                    });
                }
                // 데이터가 요청 범위(from~to)에 포함되는지 확인
                // from, to는 초 단위, bar.time은 밀리초 단위
                const fromMs = from * 1000;
                const toMs = to * 1000;
                // 첫 요청인 경우: TradingView "noData는 요청 구간에 데이터 없을 때만"
                // noData: true = 더 이상 데이터 없음(추가 요청 중단), false = 추가 요청 가능
                if (firstDataRequest) {
                    onHistoryCallback(bars, { noData: bars.length === 0 });
                    return;
                }
                // 이후 요청 (스크롤로 과거 데이터 로드)
                // 주말/휴장 등 갭 시: API가 [from,to] 밖의 마지막 거래 데이터(Friday 등)를 반환할 수 있음.
                // TradingView: "include earlier bars until countBack is reached" - 유효한 bar는 반환해야 함.
                const hasDataInRange = bars.some((bar) => bar.time >= fromMs && bar.time <= toMs);
                // 데이터가 없을 때만 noData
                if (bars.length === 0) {
                    onHistoryCallback([], { noData: true });
                    return;
                }
                // hasDataInRange가 false여도 bar 반환 (갭 시 직전 거래 데이터가 [from,to] 밖에 있을 수 있음)
                onHistoryCallback(bars, { noData: false });
            }
            catch (error) {
                console.log('[getBars]: Get error', error);
                onErrorCallback(error);
            }
        },
        subscribeBars: (symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback) => {
            currentSubscriberUID = subscriberUID;
            if (onResetCacheNeededCallback) {
                resetCacheCallback = onResetCacheNeededCallback;
                datafeed.onResetCacheNeededCallback = onResetCacheNeededCallback;
            }
            // 마지막 Bar 데이터 가져오기
            const lastBar = lastBarsCache.get(symbolInfo.full_name || symbolInfo.name);
            const lastBarForStream = lastBar
                ? {
                    ...lastBar,
                    volume: lastBar.volume || 0,
                }
                : undefined;
            // TradingView 스트리밍 구독
            subscribeOnStream(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback, lastBarForStream);
        },
        unsubscribeBars: (subscriberUID) => {
            if (currentSubscriberUID === subscriberUID)
                currentSubscriberUID = null;
            unsubscribeFromStream(subscriberUID);
        },
        unsubscribeCurrentBars() {
            if (currentSubscriberUID) {
                unsubscribeFromStream(currentSubscriberUID);
                currentSubscriberUID = null;
            }
        },
    };
    return datafeed;
}
const configurationData = {
    // Represents the resolutions for bars supported by your datafeed
    // 데이터피드에서 지원하는 봉(resolution) 목록을 나타냅니다
    // 이미지 메시지와 일치하도록 설정: "1M, 5M, 15M, 30M, 60, 240, 1D, 1W, 1M"
    supported_resolutions: ['1', '5', '30', '60', '240', '1D', '1W', '1M'],
    // The `exchanges` arguments are used for the `searchSymbols` method if a user selects the exchange
    // 사용자가 거래소를 선택할 경우 searchSymbols 메서드에서 사용되는 거래소 목록입니다
    // exchanges: [
    //   { value: 'Bitfinex', name: 'Bitfinex', desc: 'Bitfinex' },
    //   { value: 'Binance', name: 'Binance', desc: 'Binance' },
    // ],
    // The `symbols_types` arguments are used for the `searchSymbols` method if a user selects this symbol type
    // 사용자가 심볼 타입을 선택할 경우 searchSymbols 메서드에서 사용되는 심볼 타입 목록입니다
    symbols_types: [{ name: 'crypto', value: 'crypto' }],
    // Additional configuration for better price scale display
    supports_marks: false,
    supports_timescale_marks: false,
    supports_time: true,
    // chart widget 내장 검색 기능
    supports_search: false,
    supports_group_request: false,
};
/**
 * 심볼 목록 가져오기
 * TRADINGVIEW_SYMBOL_INFO_LIST 기반으로 모든 종목 반환
 */
export async function getAllSymbols() {
    // TRADINGVIEW_SYMBOL_INFO_LIST 사용
    return Object.values(TRADINGVIEW_SYMBOL_INFO_LIST).map((symbolInfo) => ({
        symbol: symbolInfo.ticker,
        ticker: symbolInfo.ticker,
        description: symbolInfo.description,
        type: symbolInfo.type,
        exchange: symbolInfo.exchange,
    }));
}
export default createDatafeed;
//# sourceMappingURL=datafeed.js.map